import { useNuxtApp, type AsyncDataOptions } from '#app'
import { useClock, useDayjs } from '#imports'
import type { UseQueryCacheReturn } from '../../types/api'

/** The `payload.data` entry this cache keeps its own freshness stamps under. */
export const QUERY_CACHE_KEY = 'queryCache'

export const useQueryCache = <TData = any>(): UseQueryCacheReturn<TData> => {
    const nuxtApp = useNuxtApp()
    const dayjs = useDayjs()
    const { clock } = useClock()

    function get(key: string) {
        // `null` is a cached answer (e.g. a 204 "no hero"); only `undefined` is a miss.
        const data: TData | undefined = key in nuxtApp.payload.data ? nuxtApp.payload.data[key] : nuxtApp.static.data[key]

        if (data === undefined) {
            return
        }

        const cache = nuxtApp.payload.data[QUERY_CACHE_KEY]?.[key]

        if (!cache?.expiresAt) return

        // Freshness must use the server clock: a device clock skewed ahead of
        // expiresAt (written from server time at SSR) would mark every hydrated
        // query expired and refetch mid-hydration, crashing the async boundary.
        const now = dayjs(clock.value)
        const expiresAt = dayjs.utc(cache.expiresAt)
        const isExpired = now >= expiresAt

        if (isExpired) return

        return data as TData
    }

    /**
     * Whether this cache has ever stamped `key`. A stamp that has been `unset` still
     * counts: it marks the key as this layer's, which is what tells a sweep apart from
     * async data some other caller owns and must not touch.
     */
    function owns(key: string): boolean {
        const stamps = nuxtApp.payload.data[QUERY_CACHE_KEY]

        return !!stamps && key in stamps
    }

    /** Drops the stamps of keys that are gone for good, so they stop accumulating. */
    function forget(keys: string[]) {
        const stamps = nuxtApp.payload.data[QUERY_CACHE_KEY]

        if (!stamps) return

        for (const key of keys) delete stamps[key]
    }

    function unset(keys: string | string[]) {
        const _handler = (key: string) => {
            const data = nuxtApp.payload.data?.[QUERY_CACHE_KEY]?.[key]

            if (!data) return

            nuxtApp.payload.data[QUERY_CACHE_KEY][key] = undefined
        }

        if (Array.isArray(keys)) {
            keys.forEach(key => _handler(key))
            return
        }

        _handler(keys)
    }

    /**
     * Helper function for setting up `getCachedData` in `useAsyncData`'s options.
     * @param key A key
     */
    const getCachedDataHandler: AsyncDataOptions<TData>['getCachedData'] = (key, _, context) => {
        if (context.cause === 'refresh:manual') return
        if (context.cause === 'refresh:hook') return

        const cachedData = get(key)

        return cachedData
    }

    function transformHandler(key: string, input: TData, ttl: number): TData {
        const now = dayjs(clock.value)

        const cache = {
            expiresAt: now.add(dayjs.duration({ seconds: ttl })).toISOString(),
        }

        nuxtApp.payload.data[QUERY_CACHE_KEY] ||= {}
        nuxtApp.payload.data[QUERY_CACHE_KEY][key] = cache

        return input
    }

    return {
        get,
        owns,
        forget,
        unset,
        getCachedDataHandler,
        transformHandler,
    }
}
